import { Button, Grid, Typography } from "@material-ui/core";
import { LinearBuffer } from "components/LinearbBuffer";
import DownloadTemplateButton from "components/ParcelTemplate/DownloadTemplateButton";
import UploadTemplateButton from "components/ParcelTemplate/UploadTemplateButton";
import { isValidPhoneNumberFormat } from "config/constants";
import PARCEL_TEMPLATE_HEADER from "config/parcel-template";
import { theme } from "config/theme";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { NewJobContext } from "domains/Job/context";
import { TemplateResultType } from "domains/Job/utils";
import { useOrderStyles } from "domains/Parcel/style";
import NewReceiver from "domains/Receiver/Components/NewReceiver";
import UpdateReceiver from "domains/Receiver/Components/UpdateReceiver";
import { Address, Order, Receiver } from "generated/graphql";
import { useCurrentUser } from "hooks";
import { OrderIcon, OrderIconPhone } from "icons";
import React, { useMemo } from "react";
import "styled-components/macro";
import ActionsGroup from "ui/ActionGroup";
import { parseTemplate, uuid } from "utils";
import OrderCollapse from "./NewOrderOverview";

interface IProps {
  onNext?: React.Dispatch<React.SetStateAction<number>>;
  cancelUpdate: () => void;
}

export function NewOrdersList({ onNext, cancelUpdate }: IProps) {
  const { user } = useCurrentUser();
  const { orders, setOrders, setJob } = React.useContext(NewJobContext);
  const [open, setOpen] = React.useState(false);
  const [openUpdate, setOpenUpdate] = React.useState(false);
  const [order, setOrder] = React.useState<Order>({ order_parcels: [], order_receiver: {} });
  const [openNewParcel, setOpenNewParcel] = React.useState(false);
  const styles = useOrderStyles();
  const dispatchAlert = useAlertDispatch();
  const { text } = useText();
  const [isUploading, setIsUploading] = React.useState(false);
  const uploadTemplateButtonRef = React.useRef<HTMLButtonElement>();
  const downloadTemplateButtonRef = React.useRef<HTMLButtonElement>();

  const UpdateOrder = (order: Order) => {
    const list: Order[] = orders;
    const index = list.findIndex((e) => e?._id === order?._id);
    if (index !== -1) {
      list.splice(index, 1);
    }
    return [order, ...list];
  };
  const handleUpdate = (upOrder: Order) => {
    const list: Order[] = UpdateOrder(upOrder);
    setOrders(list);
  };

  const hasInvalidReceiver = (receiver: Receiver) =>
    !!Object.keys(receiver).find(
      (key) =>
        key !== "receiver_entreprise" &&
        key !== "receiver_specification_address" &&
        key !== "receiver_address" &&
        key !== "receiver_email" &&
        (receiver[key] === undefined || receiver[key] === null || receiver[key]?.length === 0)
    );
  const isEmpty = (value: string) => !value || !value.length;
  const hasInvalidAddress = (address: Address) =>
    !address || address === undefined || address?.place_id?.length === 0 || address?.address?.length === 0;

  const hasInvalidParcels = (orderTest: Order) =>
    !orderTest?.order_parcels.length ||
    !!orderTest?.order_parcels?.find(
      // eslint-disable-next-line camelcase
      ({ parcel_description, parcel_size }) => isEmpty(parcel_description) || isEmpty(parcel_size)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isWarring = (view: Order) => {
    const receiver: Receiver = view?.order_receiver;
    const address: Address = receiver?.receiver_address;
    return (
      hasInvalidAddress(address) ||
      !isValidPhoneNumberFormat(receiver.receiver_phone_number) ||
      hasInvalidReceiver(receiver) ||
      hasInvalidParcels(view)
    );
  };

  const isOktoGoNext = React.useMemo(() => {
    let isOk = true;
    if (orders?.length === 0) return false;
    orders?.forEach((element) => {
      const value = isWarring(element);
      if (value === true) {
        isOk = false;
      }
      if (element.order_parcels?.length === 0) {
        isOk = false;
      }
      const parcel = element.order_parcels?.find(
        // eslint-disable-next-line camelcase
        ({ parcel_description, parcel_size }) => isEmpty(parcel_description) || isEmpty(parcel_size)
      );
      if (parcel) {
        isOk = false;
      }
    });

    return isOk;
  }, [isWarring, orders]);

  const handleUploadOrder = ({ data }: TemplateResultType) => {
    setOrders(data);
  };

  const handleDelete = (order: Order) => {
    const list: Order[] = orders.filter((o) => o._id !== order._id);
    setOrders(list);
  };

  const handleDuplicate = (data: Order) => {
    const newOrders: Order = {
      _id: uuid(),
      order_receiver: {
        ...data.order_receiver,
        receiver_phone_number: "",
      },
      order_parcels: order.order_parcels,
    };
    setOrders([newOrders, ...orders]);
  };

  const handleAddParcel = (data: Order) => {
    setOrder(data);
    setOpenNewParcel(true);
  };
  const handleEditParcel = (data: Order) => {
    setOrder(data);
    setOpenUpdate(true);
  };

  const actionsGroups = [
    { key: "1", label: text.all.add_new_order },
    { key: "2", label: text.all.upload_template },
    { key: "3", label: text.all.download_template },
  ];

  const handleActionsClick = (key: string) => {
    switch (key) {
      case "1":
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        onNewOrder();
        break;
      case "2":
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        handleClickUpload();
        break;
      case "3":
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        handleClickDawnload();
        break;
      default:
        break;
    }
  };

  const onNewOrder = () => setOpen(true);
  const handleClickUpload = () => {
    uploadTemplateButtonRef?.current?.click();
  };

  const handleClickDawnload = () => {
    downloadTemplateButtonRef?.current?.click();
  };

  return (
    <>
      <div className={styles.root}>
        {!isUploading ? (
          <Grid className={styles.rootContent}>
            <Grid className={styles.title}>
              <Typography variant="h2" className={styles.step}>
                {text.all.step} 2
              </Typography>
            </Grid>
            <Grid className={styles.line} spacing={4}>
              <Grid className={styles.jobType}>{text.parcel.add_client_to_be_delivered}</Grid>
            </Grid>
            <Grid item container xs={6} justifyContent="flex-end" className={styles.addOrder}>
              <ActionsGroup actions={actionsGroups} onClick={handleActionsClick} buttonName={text.all.add_new_order} />

              <Grid style={{ display: "none" }}>
                <DownloadTemplateButton
                  variant="outlined"
                  css={`
                    margin-right: 10px;
                    &.MuiButton-outlined {
                      border: 1px solid ${theme.palette.primary.main};
                      color: ${theme.palette.primary.main};
                    }
                  `}
                  buttonRef={downloadTemplateButtonRef}
                >
                  {text.all.download_template}
                </DownloadTemplateButton>
                <UploadTemplateButton
                  isloadingFunc={setIsUploading}
                  parseActionFunc={parseTemplate}
                  templateType={PARCEL_TEMPLATE_HEADER}
                  variant="contained"
                  color="primary"
                  entityList={orders}
                  onUpload={(uploadOrders) => handleUploadOrder(uploadOrders)}
                  buttonRef={uploadTemplateButtonRef}
                >
                  {text.all.upload_template}
                </UploadTemplateButton>
              </Grid>
            </Grid>

            <Grid className={styles.ordersDisplay}>
              {!orders.length && (
                <div className={styles.orderIcon}>
                  {[theme.breakpoints.down("xs")] ? <OrderIconPhone /> : <OrderIcon />}

                  <Grid className={styles.orderTextInfo}>{text.all.orderTextInfo}</Grid>
                </div>
              )}

              {orders?.map((view: Order, index: number) => (
                <Grid
                  css={`
                    width: 117.7%;
                  `}
                >
                  <OrderCollapse
                    warring={isWarring(view)}
                    order={view}
                    addParcel={handleAddParcel}
                    menu
                    onDelete={(data) => handleDelete(data)}
                    onEdit={(data) => handleEditParcel(data)}
                    onDuplicate={(data) => handleDuplicate(data)}
                    openNewParcel={openNewParcel}
                    setOpenNewParcel={setOpenNewParcel}
                    handleUpdate={handleUpdate}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Grid className={styles.rootContent}>
            <LinearBuffer />
          </Grid>
        )}
        <Grid className={styles.buttons}>
          <Button
            variant="outlined"
            css={`
              &.MuiButton-root {
                width: 160px;
                margin-left: auto;
              }
              margin-top: 5px;
            `}
            onClick={() => onNext(0)}
          >
            {text.all.cancel}
          </Button>

          <Button
            color="primary"
            variant="outlined"
            css={`
              &.MuiButton-root {
                width: 160px;
                margin-left: auto;
              }
              margin-top: 5px;
            `}
            onClick={() => onNext(0)}
          >
            {text.all.previous}
          </Button>
          <Button
            color="primary"
            variant="contained"
            css={`
              margin-top: 5px;
              &.MuiButton-root {
                width: 160px;
                margin-left: 20px;
              }
            `}
            onClick={() => onNext(2)}
            disabled={!isOktoGoNext}
          >
            {text.all.next}
          </Button>
        </Grid>
      </div>

      <NewReceiver
        open={open}
        clientId={user?.company_user_clientId}
        handleClose={() => setOpen(false)}
        key="receiver modal"
      />

      <UpdateReceiver
        open={openUpdate}
        handleClose={() => setOpenUpdate(false)}
        handlerUpdate={handleUpdate}
        order={order}
        clientId={user?.company_user_clientId}
      />
    </>
  );
}

export default NewOrdersList;
